import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFilmById, getDatiAzienda } from '../services/api';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import '../print.css';

const FilmPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [film, setFilm] = useState(null);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFilmAndAziendaData = async () => {
      try {
        setLoading(true);
        const [filmResponse, aziendaResponse] = await Promise.all([
          getFilmById(id),
          getDatiAzienda()
        ]);
        setFilm(filmResponse.data);
        setDatiAzienda(aziendaResponse.data);
      } catch (err) {
        setError('Errore nel caricamento dei dati per la stampa.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchFilmAndAziendaData();
  }, [id]);

  useEffect(() => {
    const handleAfterPrint = () => {
      // Introduce a small delay to ensure the print dialog is fully dismissed
      setTimeout(() => {
        navigate('/film');
      }, 100);
    };

    if (!loading && !error && film && datiAzienda) {
      window.addEventListener('afterprint', handleAfterPrint);
      window.print();
    }

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [loading, error, film, datiAzienda, navigate]);

  const renderPrintField = (label, value) => (
    <div className="print-field">
      <span className="label">{label}:</span>
      <span className="value">{value || 'N/D'}</span>
    </div>
  );

  if (loading) return <Container className="text-center mt-5"><Spinner animation="border" /><p>Preparazione stampa film...</p></Container>;
  if (error) return <Container className="mt-5"><Alert variant="danger">{error}</Alert></Container>;
  if (!film || !datiAzienda) return <Container className="mt-5"><Alert variant="warning">Dati del film o dell'azienda non trovati.</Alert></Container>;

  return (
    <div id="printable-area" className="portrait-page">
      <div className="print-container">
        <header className="print-header">
          <div className="company-details">
            {datiAzienda.logo_url && (
              <img
                src={`${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}`}
                alt="Logo Azienda"
                style={{ maxHeight: '150px', maxWidth: '150px', marginRight: '20px', objectFit: 'contain' }}
              />
            )}
          </div>
          <div className="print-info">
            <p><strong>Data di Stampa:</strong> {new Date().toLocaleDateString()}</p>
            <p><strong>ID Film:</strong> {film.id}</p>
            <p><strong>SKU:</strong> {film.sku}</p>
          </div>
        </header>

        <main>
          <h2 className="document-title text-center">{film.titolo}</h2>

          {/* ANTEPRIMA IMMAGINE */}
          {film.immagine_url && (
            <div className="text-center mb-4">
              <img
                src={process.env.REACT_APP_STATIC_FILES_BASE_URL + film.immagine_url}
                alt={"Copertina di " + film.titolo}
                style={{ maxHeight: '240px', border: '1px solid #dee2e6', borderRadius: '8px' }}
              />
            </div>
          )}

          {/* SCHEDA FILM */}
          <section className="print-section">
            <div className="print-section-title">SCHEDA FILM</div>
            <div className="print-section-content">
              {renderPrintField('Titolo', film.titolo)}
              {renderPrintField('Anno', film.anno)}
              {renderPrintField('Durata', film.durata ? `${film.durata} min.` : 'N/D')}
              {renderPrintField('Regista', film.regista)}
              {renderPrintField('Genere', film.genere)}
              {renderPrintField('Casa di Produzione', film.casa_di_produzione)}
              <hr />
              <p className="label">Attori:</p>
              <p className="value">{film.attori || 'N/D'}</p>
              <hr />
              <p className="label">Trama:</p>
              <p className="value">{film.trama || 'N/D'}</p>
            </div>
          </section>

          {/* DETTAGLI PRODOTTO */}
          <section className="print-section">
            <div className="print-section-title">DETTAGLI PRODOTTO</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>{renderPrintField('Categoria', film.categoria)}</Col>
                <Col xs={6}>{renderPrintField('Supporto', film.supporto)}</Col>
              </Row>
              <Row>
                <Col xs={4}>{renderPrintField('SKU', film.sku)}</Col>
                <Col xs={4}>{renderPrintField('Barcode', film.barcode)}</Col>
                <Col xs={4}>{renderPrintField('Barcode IBS', film.barcode_ibs)}</Col>
              </Row>
              {renderPrintField('Ubicazione', film.ubicazione)}
            </div>
          </section>

          {/* PREZZI E MAGAZZINO */}
          <section className="print-section">
            <div className="print-section-title">PREZZI E MAGAZZINO</div>
            <div className="print-section-content">
              <Row>
                <Col xs={6}>{renderPrintField('Prezzo Negozio', film.prezzo_negozio ? `€ ${film.prezzo_negozio.toFixed(2)}` : 'N/D')}</Col>
                <Col xs={6}>{renderPrintField('Prezzo IBS', film.prezzo_ibs ? `€ ${film.prezzo_ibs.toFixed(2)}` : 'N/D')}</Col>
              </Row>
              <Row>
                <Col xs={6}>{renderPrintField('Costo Acquisto', film.costo_acquisto ? `€ ${film.costo_acquisto.toFixed(2)}` : 'N/D')}</Col>
                <Col xs={6}>{renderPrintField('IVA', film.iva ? `${film.iva}%` : 'N/D')}</Col>
              </Row>
              <Row>
                <Col xs={6}>{renderPrintField('Condizione', film.condizione)}</Col>
                <Col xs={6}>{renderPrintField('Quantità', film.quantita)}</Col>
              </Row>
              {renderPrintField('Fornitore', film.fornitore?.denominazione)}
            </div>
          </section>

          {/* CATALOGO */}
          <section className="print-section">
            <div className="print-section-title">CATALOGO E WEB</div>
            <div className="print-section-content">
              <Row>
                <Col xs={4}>{renderPrintField('Versione Noleggio', film.versione_noleggio ? 'Sì' : 'No')}</Col>
                <Col xs={4}>{renderPrintField('Fuori Catalogo', film.fuori_catalogo ? 'Sì' : 'No')}</Col>
                <Col xs={4}>{renderPrintField('Pubblicato su IBS', film.da_pubblicare_su_ibs ? 'Sì' : 'No')}</Col>
              </Row>
              {film.descrizione_web && (
                <>
                  <hr />
                  <p className="label">Descrizione WEB:</p>
                  <div className="value" dangerouslySetInnerHTML={{ __html: film.descrizione_web }} />
                </>
              )}
            </div>
          </section>
        </main>

        <footer className="print-footer">
          <p>{datiAzienda.denominazione} - {datiAzienda.indirizzo} - {datiAzienda.cap} {datiAzienda.citta} ({datiAzienda.provincia}) - P.IVA {datiAzienda.piva}</p>
          <p>Email: {datiAzienda.email} - Tel: {datiAzienda.tel}</p>
        </footer>
      </div>
    </div>
  );
};

export default FilmPrint;